import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/vendor-registartion-form',
      name: 'vendor-registration',
      component: () => import('@/views/VendorRegistrationView.vue'),
      meta: { public: true },
    },
    {
      path: '/vendor-registration-form',
      redirect: (to) => ({ name: 'vendor-registration', query: to.query }),
    },
    {
      // Auth callback route - handles OAuth2 redirect from Entra
      // The actual auth callback is handled by the backend at /auth/callback
      // This route is just for any frontend-based error handling
      path: '/auth/callback',
      name: 'auth-callback',
      redirect: (to) => {
        // If there's an error, redirect to login with error
        if (to.query.error) {
          return {
            name: 'login',
            query: {
              error: to.query.error,
              message: to.query.error_description || to.query.error
            }
          }
        }
        // Otherwise, let the backend handle it
        return { name: 'dashboard' }
      },
      meta: { public: true },
    },
    {
      // Public credential viewing page (one-time link for clients - legacy)
      path: '/credentials/:token',
      name: 'credential-view',
      component: () => import('@/views/CredentialViewPage.vue'),
      meta: { public: true },
    },
    {
      // Back-compat: older links used /consumer-credentials/:token — same page as /credentials/:token
      path: '/consumer-credentials/:token',
      redirect: (to) => ({
      name: 'credential-view',
      params: { token: to.params.token as string },
      }),
    },
    {
      // Public MCP credential viewing page (one-time link)
      path: '/mcp-credentials/:token',
      name: 'mcp-credential-view',
      component: () => import('@/views/MCPCredentialViewPage.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/instances',
      name: 'instances',
      component: () => import('@/views/InstancesView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/instances/:accountId',
      name: 'instance-detail',
      component: () => import('@/views/InstanceDetailView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/credentials',
      name: 'credentials',
      component: () => import('@/views/CredentialsView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/credentials/instance/:accountId',
      name: 'credentials-instance',
      component: () => import('@/views/CredentialDetailView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/operations',
      name: 'operations',
      component: () => import('@/views/OperationsView.vue'),
    },
    {
      path: '/stats',
      name: 'stats',
      component: () => import('@/views/StatsView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/stats/instance/:accountId',
      name: 'stats-instance',
      component: () => import('@/views/StatsView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/internal-apps',
      name: 'internal-apps',
      component: () => import('@/views/InternalAppsView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/mcp-consumers',
      name: 'mcp-consumers',
      component: () => import('@/views/MCPConsumersView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/mcp-consumers/:id',
      name: 'mcp-consumer-detail',
      component: () => import('@/views/MCPConsumerDetailView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: () => import('@/views/JobsView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/help',
      name: 'help',
      component: () => import('@/views/DocsRedirectView.vue'),
      meta: { public: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
    },
    {
      path: '/reconciliation',
      name: 'reconciliation',
      component: () => import('@/views/ReconciliationView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/consumers',
      name: 'consumers',
      component: () => import('@/views/ConsumersView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    {
      path: '/consumers/:id',
      name: 'consumer-detail',
      component: () => import('@/views/ConsumerDetailView.vue'),
      meta: { roles: ['ADMIN', 'SUPPORT'] },
    },
    // Legacy redirects
    {
      path: '/environments',
      redirect: '/',
    },
    {
      path: '/audit',
      redirect: '/operations',
    },
  ],
})

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Allow public routes without auth check
  if (to.meta.public) {
    // If authenticated and going to login, redirect to dashboard
    if (authStore.isAuthenticated && to.name === 'login') {
      return next({ name: 'dashboard' })
    }
    return next()
  }

  // Check authentication status with the backend if not checked yet
  if (!authStore.authChecked) {
    await authStore.init()
  } else if (authStore.isAuthenticated) {
    // Re-sync role from DB on navigation (live RBAC after role changes)
    await authStore.checkAuth()
  }

  // Check authentication
  if (!authStore.isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Check role-based access
  const allowedRoles = to.meta.roles as string[] | undefined
  if (allowedRoles && !authStore.hasRole(allowedRoles)) {
    return next({ name: 'dashboard' })
  }

  next()
})

export default router
